import React, { FC } from "react";
import styles from "./SignupHeader.module.css";
import Image from "next/image";
import { CalendlyPopUp } from "components/ContentfulRenderer/ContentfulComponent/Calendly/PopUp";
import useBreakPoints from "hooks/useMediaQuery";
import { useWebTranslation } from "translations";
import { getMeetingBookingUrl } from "utils/site";
import { Icon } from "@/ui-kit";
import Gutter from "ui-kit/Gutter";
import { Typography } from "ui-kit/Typography";
import classNames from "classnames";
import { useNavigation } from "components/Signup/store/selectors/navigation";
import { StepName } from "components/Signup/steps/types";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";

type SignupHeaderProps = {
  previous: (replace?: boolean) => void;
  showBackButton?: boolean;
  showPhoneButton?: boolean;
};

const useHeadline = ({
  activeStep,
  name,
}: {
  activeStep: StepName;
  name: string | null;
}) => {
  const { t } = useWebTranslation();

  const translations = t("signup.header.step", {
    returnObjects: true,
  }) as unknown as Record<string, string>;
  const headline = translations[activeStep];

  return (
    headline ??
    (name
      ? t("signup.header.about_name", { name })
      : t("signup.header.about_your_pet"))
  );
};

const SignUpHeader: FC<SignupHeaderProps> = ({
  previous,
  showBackButton = true,
  showPhoneButton = false,
}) => {
  const { isMobile } = useBreakPoints();
  const { t } = useWebTranslation();
  const animal = useCurrentAnimal();
  const { activeStep } = useNavigation();

  const meetingBookingUrl = getMeetingBookingUrl();

  const headline = useHeadline({ name: animal?.name, activeStep });

  return (
    <header
      className={styles.outerContainer}
      style={{
        justifyContent:
          isMobile && showPhoneButton ? "flex-start" : "space-between",
      }}
    >
      <div className={styles.topRow}>
        <div>
          {showBackButton ? (
            <button
              className={classNames(
                styles.backContainer,
                styles.backContainerDesktop
              )}
              onClick={() => previous(true)}
              aria-label={t("global.back")}
            >
              <Icon
                name="chevron-left"
                size="M"
                className={styles.backButton}
                ariaHidden
              />
            </button>
          ) : null}
          <Image
            src="/images/logo-with-name.svg"
            className={styles.logo}
            alt="Lassie"
            height={25}
            width={105}
          />
        </div>
        {headline ? (
          <div className={styles.headline}>
            <Typography variant="body3" component="h1">
              <strong>{headline}</strong>
            </Typography>
          </div>
        ) : null}
        {showPhoneButton ? (
          <div>
            {meetingBookingUrl ? (
              <CalendlyPopUp
                meetingBookingUrl={meetingBookingUrl}
                buttonTitle={t("global.call_me")}
                onClickEvent="SIGNUP_CALL_ME"
                small
              />
            ) : null}
          </div>
        ) : (
          <Gutter horizontal size={4} />
        )}
      </div>
      <div className={styles.bottomRow}>
        {showBackButton ? (
          <button
            className={classNames(
              styles.backContainer,
              styles.backContainerMobile
            )}
            // 2024-10-04: Refactored into redux: This is not necessarily correct but made what was happening explicit
            onClick={() => previous(true)}
            aria-label={t("global.back")}
          >
            <Icon
              name="chevron-left"
              size="M"
              className={styles.backButton}
              ariaHidden
            />
          </button>
        ) : null}
        {headline ? (
          <div>
            <Typography variant="body3" component="h1">
              <strong>{headline}</strong>
            </Typography>
          </div>
        ) : null}
      </div>
    </header>
  );
};

export default SignUpHeader;
